import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SITE } from "@/data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Website inquiry from ${form.name || "customer"}`);
    const body = encodeURIComponent(`${form.message}\n\nFrom: ${form.name}\nEmail: ${form.email}`);
    window.location.href = `${SITE.emailHref}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app", { description: "You can also call us directly anytime." });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
      <Seo
        title="Contact CA HVAC Direct | Help Choosing HVAC Equipment"
        description="Contact CA HVAC Direct for help choosing HVAC equipment or questions before ordering."
        path="/contact"
      />
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-sky-700">Contact</span>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
          Get in Touch
        </h1>
        <p className="mt-4 text-slate-600">
          Need help choosing equipment or have a question before ordering? Call or email us — a real
          person will help you find the right HVAC equipment.
        </p>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        {/* Contact details */}
        <div className="space-y-4">
          <a href={SITE.phoneHref} className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-6 transition-colors duration-200 hover:border-sky-700" data-testid="contact-phone">
            <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-sky-50 text-sky-700"><Phone className="h-5 w-5" /></span>
            <span>
              <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Phone</span>
              <span className="mt-1 block font-heading text-xl font-bold text-slate-900">{SITE.phone}</span>
              <span className="text-xs text-slate-500">Tap to call on mobile</span>
            </span>
          </a>

          <a href={SITE.emailHref} className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-6 transition-colors duration-200 hover:border-sky-700" data-testid="contact-email">
            <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-sky-50 text-sky-700"><Mail className="h-5 w-5" /></span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Email</span>
              <span className="mt-1 block break-all font-heading text-lg font-bold text-slate-900">{SITE.email}</span>
              <span className="text-xs text-slate-500">Tap to email</span>
            </span>
          </a>

          <div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-sky-50 text-sky-700"><Clock className="h-5 w-5" /></span>
            <span>
              <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Business Hours</span>
              <span className="mt-1 block text-slate-600">To be confirmed</span>
              <span className="text-xs text-amber-600">Editable placeholder — hours to be added</span>
            </span>
          </div>
          
<div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-6">
  <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-sky-50 text-sky-700">
    <MapPin className="h-5 w-5" />
  </span>
  <span>
    <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Local Pickup</span>
    <span className="mt-1 block font-heading text-lg font-bold text-slate-900">West Covina, CA</span>
    <span className="text-xs text-slate-500">Contact us to confirm availability and arrange pickup.</span>
  </span>
</div>
  
        </div>

        {/* Form */}
        <form onSubmit={submit} className="rounded-sm border border-slate-200 bg-white p-6" data-testid="contact-form">
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Send a Message</h2>
          <div className="mt-5 space-y-4">
            <div>
              <Label htmlFor="c-name" className="text-sm text-slate-700">Name</Label>
              <Input id="c-name" value={form.name} onChange={update("name")} placeholder="Your name" required className="mt-1.5 rounded-sm border-slate-300" data-testid="contact-name" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-sm text-slate-700">Email</Label>
              <Input id="c-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" required className="mt-1.5 rounded-sm border-slate-300" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="c-message" className="text-sm text-slate-700">Message</Label>
              <Textarea id="c-message" value={form.message} onChange={update("message")} placeholder="How can we help?" required className="mt-1.5 min-h-[120px] rounded-sm border-slate-300" data-testid="contact-message" />
            </div>
            <Button type="submit" className="w-full rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="contact-submit">
              <Send className="h-4 w-4" /> Send Message
            </Button>
            <p className="text-center text-xs text-slate-400">Opens your email app addressed to us. You can also call anytime.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
